import { createAction, handleActions } from "redux-actions";
import { actionAddAlert } from "../Alert";
import { jwtDecode } from "jwt-decode";

const HASH_REDIRECT_KEY = "hashredirect";

const initialState = {
  auth: null,
  user: null,
  status: null, // authenticated, failed, expired
  idToken: null,
  showAttestation: null,
};

export const actionAuthUpdated = createAction("APP_USER_AUTH_UPDATED");
export const actionAuthSignedIn = createAction("APP_USER_SIGNED_IN");
export const actionAuthNotAuthorized = createAction("APP_USER_NOT_AUTHORIZED");
export const actionAuthSessionExpired = createAction(
  "APP_USER_EXPIRED_SESSION",
);
export const actionAttestationUpdated = createAction("APP_ATTESTATION_UPDATED");

export const startLoginFlow = (auth) => {
  return (_dispatch, _getState) => {
    if (window.location.hash) {
      window.localStorage.setItem(HASH_REDIRECT_KEY, window.location.hash);
    } else {
      window.localStorage.removeItem(HASH_REDIRECT_KEY);
    }
    auth.signinRedirect();
  };
};

// Global variables are bad. This is needed so we can access our
// JWT from an axios hook.
let currentIdToken;
export const getCurrentIdToken = () => {
  return currentIdToken;
};

// Used only for automated testing (playwright).
window.mgis_getCurrentIdToken = getCurrentIdToken;

const setCurrentIdToken = (token) => {
  currentIdToken = token;
};

export const actionAuthSignInUser = (user) => {
  return (dispatch, _getState) => {
    const hashRedirect = window.localStorage.getItem(HASH_REDIRECT_KEY);
    window.localStorage.removeItem(HASH_REDIRECT_KEY);

    if (hashRedirect) {
      // Delay this a bit to ensure the hook in UrlSearch.jsx is setup.
      window.setTimeout(() => {
        window.location.hash = hashRedirect;
      }, 100);
    }

    const idToken = user.id_token;

    // old expired DEV token, convenient for testing
    // const idToken = "eyJraWQiOiJhcWFmNmZodWIrb2J1VVhGaExcL1RBR3BQMVZwc1ZTbXFObjRwTEJIMms2QT0iLCJhbGciOiJSUzI1NiJ9.eyJhdF9oYXNoIjoiTWQ4ZTRxcnpLdGpJZDR4TURxVjdrdyIsInN1YiI6ImQyMWFjMDJmLWY0YjMtNGYwNy05OTE2LTk0MmNmODU1MjU1YyIsImNvZ25pdG86Z3JvdXBzIjpbInVzLWVhc3QtMV9mcGd5U2VuZzZfdW1pY2guZWR1IiwibWdpcy11c2VycyJdLCJpc3MiOiJodHRwczpcL1wvY29nbml0by1pZHAudXMtZWFzdC0xLmFtYXpvbmF3cy5jb21cL3VzLWVhc3QtMV9mcGd5U2VuZzYiLCJjb2duaXRvOnVzZXJuYW1lIjoidW1pY2guZWR1X3NhbWJyb3ciLCJub25jZSI6Imc2TVV2cmdVcHg4VXpoOUt0dXZjYVJ2RVJMdmRVSy03ZTJPTm5PNkdkR3ROWGlzNXZDU3BIUmd3NElzaVZZQVdEYUh3Z2xZazVseGJYMFdEX3d0MG5oNXJBUUVtOG9nVmtLZFJ2bkt1WUdvY3ZTTWFDaDNmUTlaQ29KNlVkOGxRWGFfMmQ1X3pLWUFqY0l6U041S3ZVOVE2WlkyYjNJWlNxSWdkVF9DVi1fYyIsImF1ZCI6IjIyYm9vaHYxMXBmbDk4Zmpndjh1bGJtODdtIiwiaWRlbnRpdGllcyI6W3sidXNlcklkIjoic2FtYnJvdyIsInByb3ZpZGVyTmFtZSI6InVtaWNoLmVkdSIsInByb3ZpZGVyVHlwZSI6Ik9JREMiLCJpc3N1ZXIiOm51bGwsInByaW1hcnkiOiJ0cnVlIiwiZGF0ZUNyZWF0ZWQiOiIxNzQxMDE5NTkyMzI4In1dLCJ0b2tlbl91c2UiOiJpZCIsImF1dGhfdGltZSI6MTc0MTIwOTAzOCwiZXhwIjoxNzQxMjE5ODM4LCJpYXQiOjE3NDEyMDkwMzgsImVtYWlsIjoic2FtYnJvd0B1bWljaC5lZHUifQ.C3_335kRpsBsIn2yUkKAL0Cy27RH2BTCvp5Fg64NFyfVyrK7iOR4A5LN8I2yxEsaYT3Z_LcIti5AOuwVscH38q7H2Oe7XAfTOjyzyeC30z-cgZoA5z33XEmUHpI06_YupPOMI8egFCf8vuh1hwAZ63yYecFnQPj1w0jyobipMe7usRyo48dQgnFMunvKeU2bIm7rhbiMTiI1qH_Ua5lEfh5N_PA0zP4D8aGnByHkLzCs6k17p-KCDZi0Z_KC9nDvo8TFNyDPnB1ZDhGuTQgj2XTdX9udBlitjKdLGBM-17HCFgcb3HBvOYFS04td6h6KWdtwBCptsEtapc376IR4Tg"

    dispatch(actionAuthSignedIn({ user, idToken }));
  };
};

export const actionAuthSignOutUser = () => {
  /*
  Logging out is complicated. Here are the things that must happen:

  1. remove JWTs from local storage
  2. call the /logout endpoint of our OIDC domain
  3. call https://okta.umich.edu/login/signout

  Each of the above contains a "memory" of our login session. All must
  be removed or else the user can likely keep using the app without
  re-providing credentials.

  We can do #1 directly by calling: auth.removeUser()

  We then do #2 by redirecting our app to our OIDC/logout URL.

  We handle #3 by passing along a logout_uri query parameter set to
  okta. OIDC/logout will do its work and then redirect to
  okta.

  Note: The backend (mba/cognito) has already been altered to allow
        this okta URL as an allowed logout url.
   */
  return (dispatch, getState) => {
    // We must remove any tokens in storage to avoid our auto-login
    // logic from being invoked after auth.removeUser() and before
    // the new URL loads.
    removeLocalStorageTokens();
    const auth = getState().authReducer.auth;
    auth.removeUser().then(() => {
      window.location.href = `${auth.settings.metadata.end_session_endpoint}?client_id=${auth.settings.client_id}&logout_uri=${import.meta.env.VITE_AUTH_LOGOUT_URL}`;
    });
  };
};

export const jwtIsExpired = (token) => {
  let jwtIsExpired = false;
  if (token) {
    try {
      const jwtPayload = jwtDecode(token);
      const unixTimeSeconds = Math.floor(Date.now() / 1000);
      jwtIsExpired = jwtPayload.exp < unixTimeSeconds;
    } catch (error) {
      jwtIsExpired = true;
      console.error("Unexpected JWT failure: ", error);
    }
  }
  return jwtIsExpired;
};

export const actionAuthMapError = () => {
  return (dispatch, getState) => {
    const status = getState().authReducer.status;
    if (status === "failed") {
      // We got a map error after failing to login.
      // We purposefully don't change the status so the login
      // dialog can continue showing its login error text.
    } else {
      // We've seen false positives where mapbox fails to retrieve a
      // pbf tile file because of some other reason. Check that our
      // JWT is actually expired.
      const idToken = getState().authReducer.idToken;
      if (jwtIsExpired(idToken)) {
        dispatch(actionAuthSessionExpired());
      }
    }
  };
};

export const actionAuthShowSessionExpired = () => {
  const refreshPageHandler = () => {
    window.location.reload(false);
  };

  return (dispatch, _getState) => {
    dispatch(
      actionAddAlert({
        id: "auth",
        title: "Your session has expired. Please refresh page.",
        severity: "error",
        buttonLabel: "REFRESH",
        onClick: refreshPageHandler,
        onClose: false,
      }),
    );
  };
};

export const authReducer = handleActions(
  {
    [actionAuthUpdated]: (state, action) => {
      const { auth } = action.payload;
      return { ...state, auth: auth };
    },
    [actionAuthSignedIn]: (state, action) => {
      const { user, idToken } = action.payload;
      setCurrentIdToken(idToken);
      return { ...state, status: "authenticated", user, idToken };
    },
    [actionAuthNotAuthorized]: (state, _action) => {
      return { ...state, status: "failed", user: null };
    },
    [actionAuthSessionExpired]: (state, _action) => {
      return { ...state, status: "expired" };
    },
    [actionAttestationUpdated]: (state, action) => {
      const { showAttestation } = action.payload;
      return { ...state, showAttestation: showAttestation };
    },
  },
  initialState,
);

export function cleanUrl(keepHash = false) {
  const { origin, pathname, hash } = window.location;
  const cleanUrl = origin + pathname + (keepHash ? hash : "");
  window.history.replaceState({}, document.title, cleanUrl);
}

export function urlShowsNotAuthorized() {
  return window.location.search.search(/not(\+|%20)authorized/) !== -1;
}

export function urlShowsLoginPending() {
  return window.location.search.indexOf("code=") !== -1;
}

const LOCAL_STORAGE_OIDC_KEY_PREFIX = "oidc.user:";

function getStorageTokenKeys() {
  const keys = Object.keys(window.localStorage);
  return keys.filter((key) => key.includes(LOCAL_STORAGE_OIDC_KEY_PREFIX));
}

export function localStorageHasToken() {
  const keys = getStorageTokenKeys();
  return keys.length > 0;
}

function removeLocalStorageTokens() {
  const keys = getStorageTokenKeys();
  for (const key of keys) {
    window.localStorage.removeItem(key);
  }
}

export const selectorAuthIdToken = (state) => state.authReducer.idToken;
